<template>
<div>
  <cs-app>
      <template v-slot:content>

        <div class="spinner-wrapper" v-if="sendingForm">
          <div class="md-subheading submitting-form-text">Submitting Form</div>
          <md-progress-spinner
            class="spinner"
            md-mode="indeterminate"
            :md-diameter="100">
          </md-progress-spinner>
        </div>

        <div class="container" v-if="!sendingForm">
          <div class="md-display-1">Client Signup</div>
          <div class="md-headline info">
            Please enter the first three characters from your postal code so that we can confirm your residence within
            this jurisdiction.
          </div>
          <div class="md-layout md-gutter">
            <div class="md-layout-item">
              <md-field :class="getValidationClass('postalCode')">
                <label>Postal Code</label>
                <md-input
                  @input="$v.$touch()"
                  name="postal-code"
                  id="postal-code"
                  maxlength="3"
                  required
                  v-model="postalCode"
                ></md-input>
                <span class="md-error" v-if="!$v.postalCode.postalCodeValidator">You must enter a valid Ontario postal code.</span>
                <span class="md-helper-text">First 3 digits only</span>
              </md-field>

              <!-- TOS Agreement -->
              <div>
                <md-checkbox v-model="TOS" :value="true" id="tos-checkbox">I agree to the </md-checkbox>
                <a href="/contingentSee_Terms_of_Service.pdf" target="_blank">Terms of Service</a>
              </div>

              <!-- Privacy Policy Agreement -->
              <md-checkbox v-model="policy" :value="true" id="tos-checkbox">I agree to the </md-checkbox>
              <a href="/contingentSee_Privacy_Policy.pdf" target="_blank">Privacy Policy</a>

              <re-captcha v-if="!signupToken" ref="recaptcha" @verify="storeReCaptchaResponse" :id="1"></re-captcha>

              <div class="case-select-wrapper" v-if="selectEnabled">
                <div class="case-select-spinner" v-if="signupToken === null">
                  <md-progress-spinner
                    class="spinner"
                    md-mode="indeterminate"
                    :md-diameter="20">
                  </md-progress-spinner>
                  <span
                    style="padding-left: 10px;"
                    class="md-subheading"
                  >Loading case forms...</span>
                </div>
                <div class="case-select" v-if="signupToken">
                  <div class="md-headline info">
                    Great! Now, simply select the option that best describes the reason you are using contingentSee.
                    Don't worry if these topics seem a bit vague - you can explain more of the details about your
                    particular circumstances in the next step!
                  </div>
                  <md-field>
                    <label for="form">Select your case type</label>
                    <md-select v-model="selectedFormId" :disabled="!selectEnabled">
                      <md-option v-for="form in forms" :key="form.id" :value="form.id">
                        {{ form.title }}
                      </md-option>
                    </md-select>
                  </md-field>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
  </cs-app>
</div>
</template>


<style lang="scss">

iframe {
  height: 100% !important;
}

.md-toolbar.md-app-toolbar {
  padding-top: 10px;
}

.spinner-wrapper {
  position: absolute;
  left: 50%;
  top: 40%;
  transform: translate(-50%, -50%);
}

.submitting-form-text {
  margin-bottom: 15px;
}

.info {
  margin: 20px 0;
}

</style>

<script>
import { signupTokenService } from "@/services/signupTokenService";
import { storage } from "@/common/storage";
import { formService } from "@/services/formService";
import CSApp from "@/components/CSApp";
import { setTimeout } from 'timers';
import { geolocationService } from '@/services/geolocationService'
import { validationMixin } from 'vuelidate'
import {
  required,
  minLength,
} from 'vuelidate/lib/validators'
import ReCaptcha from '@/components/ReCaptcha'
import { isString } from 'util';
import Vue from 'vue'

const postalCodeValidator = (postalCode, vm) => {
  return geolocationService(vm.$http).validator.isValid(postalCode)
}

export default {
  name: "FormSelectPage",
  mixins: [validationMixin],
  components: {
    'cs-app': CSApp,
    ReCaptcha,
  },
  validations: {
    postalCode: {
      required,
      minLength: minLength(3),
      postalCodeValidator
    }
  },
  data() {
    return {
      forms: [],
      selectedFormId: null,
      signupToken: false,
      postalCode: "",
      TOS: false,
      policy: false,
      sendingForm: false,
      captchaResponse: "",
    };
  },
  watch: {
    selectedFormId () {
      this.showTypeFormEmbed()
    },
  },
  computed: {
    selectEnabled () {
      return !!(this.postalCodeValid && this.TOS && this.policy && this.signupToken)
    },
    postalCodeEntered () {
      return this.postalCode.length === 3
    },
    postalCodeValid () {
      return geolocationService(this.$http).validator.isValid(this.postalCode);
    }
  },
  created() {

    this.$http.get(`${process.env.VUE_APP_NETLIFY_FUNCTIONS_API}/getTypeFormForms`)
      .then(response => {
        this.forms = response.data.items
      }).catch(err => console.log(err))

    // we attempt to grab a signup token on startup because the user may already have one in
    // their local storage. If not, they'll have to complete a captcha challenge to get one.
    signupTokenService(storage, this.$http)
      .getToken(this.captchaResponse)
      .then(token => isString(token.id) ? this.signupToken = token : '');
  },
  methods: {
    storeReCaptchaResponse (response) {
      this.captchaResponse = response;

      // Now that we have a valid captcha response we can exchange it for a signup token
      // which we make required for submitting a TypeForm request
      signupTokenService(storage, this.$http)
        .getToken(this.captchaResponse)
        .then(token => isString(token.id) ? this.signupToken = token : '')
    },
    getValidationClass (fieldName) {
      const field = this.$v[fieldName]

      if (field) {
        return {
          'md-invalid': field.$invalid && field.$dirty
        }
      }
    },
    showTypeFormEmbed() {
      const hostString = process.env.VUE_APP_ENVIRONMENT !== 'production' ? `&host=${process.env.VUE_APP_API_URL}` : '';
      const reference = this.$typeformEmbed.makePopup(
        `https://developers3.typeform.com/to/${this.selectedFormId}/?form_id=${
          this.selectedFormId
        }&signup_token=${
          this.signupToken.id
        }&postal_code=${
          this.postalCode
        }${
          hostString
        }&captcha=${
          this.captchaResponse
        }`,
        {
          autoClose: 5,
          hideHeaders: true,
          hideFooter: false,
          onSubmit: this.onSubmit.bind(this),
        }
      )
      reference.open();
    },
    onSubmit () {
      this.sendingForm = true
    }
  }
};
</script>
